/* eslint-disable react-hooks/exhaustive-deps */

// React Imports
import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

// SVG Imports
import ICONS from "../../../../assets/Icons/index.js";

// Redux Imports
import {
  disableCategory,
  enableCategory,
} from "../../../../redux/features/customCategory/index.js";
import {
  updateCategotyResponse,
  getAllCategories,
} from "../../../../redux/features/sharedResources/sharedResources.js";
// Component Imports
import PrimaryWithIconButton from "../../../../components/_utils/CommonButtons/PrimaryWithIconButton/index.jsx";
import CommonCalendar from "../../../../components/_utils/CommonCalendar/index.jsx";
import MultiSelectDropDownComponent from "../../../../components/_utils/CommonDropdown/MultiSelectDropdown/index.jsx";
import CommonTimePicker from "../../../../components/_utils/CommonDropdown/SetTime/index.jsx";
import SingleSelectDropDownComponent from "../../../../components/_utils/CommonDropdown/SingleSelectDropdown/index.jsx";
import CommonFileUpload from "../../../../components/_utils/CommonFileUpload/index.jsx";
import TextInputField from "../../../../components/_utils/CommonInputFields/TextInputField.jsx";
import CommonQuantity from "../../../../components/_utils/CommonQuantity/index.jsx";
import SmallPopupWithBulletPoints from "../../../../components/_utils/CommonSmallPopupWithBulletPoint/index.jsx";
import CommonToast from "../../../../components/_utils/CommonToast/index.jsx";
import SVGComponent from "../../../../components/_utils/SVGComponent/index.jsx";
import TitleSubtitlePair from "../../../../components/_utils/TitleSubtitlePair/index.jsx";
import CommonToggleCard from "../../../../components/_utils/TransactionCommonComponents/CommonToggleCard.jsx";
import Loader from "../../../../components/Loader/Index.jsx";
import useServiceChecker from "../../../../components/ServiceChecker/index.jsx";

// Utils Imports
import {
  GlobalConfiguration,
  IntervalConfiguration,
  maxFileSize,
} from "../../../../utils/configurationProvider.js";
import { MONTHS, WEEKDAYS } from "../../../../utils/dateConstants.js";
import TEXT from "../../../../utils/globalText.js";
import GlobalArray from "../../../../utils/globalArrays.js";
import useCategories from "../../../../utils/useCategories.js";
import { globalTextFormat } from "../../../../utils/globalTextFormat.js";
import CardActionableButton from "../../../../components/_utils/CommonViewAllCard/CardSubComponent/CardActionableButton/index.jsx";
import { formatDate } from "../../../../utils/dateFormatter.js";
// ---------------------------------------------------------------------------
// Constants & pure helpers
// ---------------------------------------------------------------------------
const EDIT_CATEGORY_PATH = "/settings/category_dashboard/edit_category";
const SERVICE_NAME = "CTransactionCategory";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from(
  { length: CURRENT_YEAR - 1975 + 1 },
  (_, i) => 1975 + i,
);
const DAYS = [...WEEKDAYS.slice(1), WEEKDAYS[0]];
const STATIC_OPTIONS = { day: DAYS, month: MONTHS, year: YEARS };

const TOAST_BTN_PROPS = {
  isDisabled: false,
  enabledTextColor: "var(--text-primary)",
  enabledBgColor: "var(--primary-bg)",
};

const showToastMessage = (type,text) => {
  const checkMessageType = type === TEXT?.SUCCESS?.toLowerCase();

  CommonToast({
    text,
    icon: checkMessageType
      ? ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE
      : ICONS?.ERROR_WITH_WHITE_CIRCLE,
    variant: checkMessageType ? "var(--success)" : "var(--error)",
    btnProps: TOAST_BTN_PROPS,
  });
};

const toIsoDate = (date) =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);

// Shared props for the "Cancel" button of both confirmation popups.
const cancelButtonProps = (onCancel) => ({
  btnOneText: TEXT?.CANCEL,
  btnOneBtnType: "button",
  btnOneEnabledBgColor: "transparent",
  btnOneHandleFunction: onCancel,
  btnOneOutLineColor: "var(--text-primary)",
  btnOneEnabledTextColor: "var(--text-primary)",
  btnOneIsDisabled: false,
  btnOneHoverOption: "white",
});

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------
const Field = ({ label, className = "", children }) => (
  <div className={`flex flex-col gap-gap-xs ${className}`}>
    <p className="text-text-secondary text-font-xs">{label}</p>
    {children}
  </div>
);

const BigSelect = (props) => (
  <SingleSelectDropDownComponent
    type="big"
    placeholder={TEXT?.SELECT}
    showSearch={false}
    disabled={false}
    isOpen={false}
    {...props}
  />
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const CustomCategoryDetails = () => {
  // REACT HOOKS
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const location = useLocation();
  const categories = useSelector(
    (state) => state.sharedResources?.categoriesData?.categories,
  );
  const { categoriesLoading } = useSelector((state) => state.sharedResources);

  // CUSTOM HOOKS
  const categoryMapper = useCategories();
  const [ensureAll] = useServiceChecker();

  // STATES
  const [additionalData, setAdditionalData] = useState({});
  const [timeData, setTimeData] = useState({});
  const [dateRanges, setDateRanges] = useState({});
  const [activatePopUp, setActivatePopUp] = useState(false);
  const [deactivatePopUp, setDeactivatePopUp] = useState(false);
  const [categoryId, setCategoryId] = useState("");

  const hasCTransactionCategory = ensureAll([SERVICE_NAME]).status;
  const hasAdmin = ensureAll(["Admin"]).status;

  const category = categories?.find((data) => data?.id === +id);
  const isActive = category?.active === "Y";
  const canManage = hasAdmin || category?.custom_category === "Y";
  const fields = category?.configuration?.data;

  const categoryIcon = categoryMapper(category?.id);
  const bulkUploadCategory = location?.state?.flow;

  // Reset the preview form whenever the categories list changes.

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        await dispatch(getAllCategories());
      } catch (error) {
        console.error(error);
      }
    };

    if (bulkUploadCategory) {
      fetchCategories();
    }
  }, [dispatch, bulkUploadCategory]);

  // -------------------------------------------------------------------------
  // Field state handlers
  // -------------------------------------------------------------------------
  const setField = (name, value, dataType = "") => {
    const type = dataType.toLowerCase();
    let next = value;

    if (type === "numeric" && typeof next === "string") {
      next = next.replace(/[^0-9]/g, "");
    }
    if (
      typeof next === "string" &&
      !GlobalArray?.NON_TEXT_CATEGORY_TYPES.includes(type)
    ) {
      next = next.replace(/^\s+/, "");
    }

    setAdditionalData((prev) => ({ ...prev, [name]: next }));
  };

  const removeField = (name) => {
    setAdditionalData(({ [name]: _removed, ...rest }) => rest);
  };

  const handleFileChange = (file, field) => {
    if (file.size > maxFileSize) {
      showToastMessage(TEXT?.ERROR, TEXT?.MAXIMUM_FILE_SIZE_NOTE);
      return;
    }
    setField(field.name, file, field.data_type);
  };

  const handleTimeConfirm = (
    fieldName,
    dataType,
    hours,
    minutes,
    meridiems,
  ) => {
    setTimeData((prev) => ({
      ...prev,
      [`${fieldName}_${dataType}`]: { hours, minutes, meridiems },
    }));
    setField(fieldName, `${hours}:${minutes} ${meridiems}`, dataType);
  };

  const handleClearTime = (key, fieldName) => {
    removeField(fieldName);
    setTimeData(({ [key]: _removed, ...rest }) => rest);
  };

  const handleDateRangeChange = ([start, end], field) => {
    setDateRanges((prev) => ({
      ...prev,
      [field.name]: { start: start || null, end: end || null },
    }));

    if (start && end) {
      setField(
        field.name,
        `${toIsoDate(start)} to ${toIsoDate(end)}`,
        field.data_type,
      );
    }
  };

  // -------------------------------------------------------------------------
  // Category activate / deactivate
  // -------------------------------------------------------------------------
  const requireService = (callback) =>
    hasCTransactionCategory
      ? callback()
      : navigate("/feature_not_assigned", {
          state: { unavailableService: SERVICE_NAME },
        });

  const changeCategoryStatus = async ({
    action,
    processingText,
    successText,
    closePopUp,
  }) => {
    try {
      CommonToast({
        text: processingText,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--processing)",
      });
      toast.dismiss();

      const { payload } = await dispatch(action(id));

      if (payload?.status === true) {
        dispatch(updateCategotyResponse(payload.categories));
        showToastMessage(TEXT?.SUCCESS?.toLowerCase(), successText);
      } else {
        const errors = payload?.errors;
        const message =
          errors?.name?.[0] ?? errors?.base?.[0] ?? errors?.configuration?.[0];
        if (message) showToastMessage(TEXT?.ERROR, message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      closePopUp(false);
    }
  };

  const handleCategoryStatusChange = () =>
    changeCategoryStatus({
      action: activatePopUp ? enableCategory : disableCategory,
      processingText: activatePopUp
        ? TEXT?.ACTIVATING_CATEGORY
        : TEXT?.DEACTIVATING_CATEGORY,
      successText: activatePopUp
        ? TEXT?.CATEGORY_ACTIVATION_SUCCESS
        : TEXT?.CATEGORY_DEACTIVATION_SUCCESS,
      closePopUp: activatePopUp ? setActivatePopUp : setDeactivatePopUp,
    });

  const handleEditClick = () => {
    if (isActive) return;
    requireService(() =>
      navigate(EDIT_CATEGORY_PATH, {
        state: {
          id: category?.id,
          name: category?.name,
          description: category?.description,
          configuration: category?.configuration,
          asset_properties: {
            resource_name: category?.asset_properties?.resource_name,
            colour: category?.asset_properties?.colour,
          },
          filteredData: location?.state?.filteredData,
          flow: bulkUploadCategory,
        },
      }),
    );
  };

  const handleStatusClick = () =>
    requireService(() =>
      isActive ? setDeactivatePopUp(true) : setActivatePopUp(true),
    );

  const handleCopyIconText = () => {
    setCategoryId("category_id");
    navigator.clipboard.writeText(id);
    setTimeout(() => {
      setCategoryId("");
    }, IntervalConfiguration.intervalMedium);
  };
  // -------------------------------------------------------------------------
  // Renderers
  // -------------------------------------------------------------------------
  const renderActionButtons = (fullWidthRequired) => (
    <>
      <PrimaryWithIconButton
        btnText={TEXT?.EDIT_CATEGORY}
        btnType="button"
        isDisabled={isActive}
        handleFunction={handleEditClick}
        enabledTextColor="var(--text-primary)"
        disabledTextColor="var(--text-primary)"
        enabledBgColor="var(--primary-act)"
        disabledBgColor="var(--primary-act)"
        icon={ICONS?.EDIT_WITH_WHITE_CIRCLE}
        iconToBeAlign="right"
        fullWidthRequired={fullWidthRequired}
      />
      <PrimaryWithIconButton
        btnText={isActive ? TEXT?.DEACTIVATE_CATEGORY : TEXT?.ACTIVATE_CATEGORY}
        btnType="button"
        handleFunction={handleStatusClick}
        enabledTextColor="var(--text-primary)"
        disabledTextColor="var(--text-primary)"
        enabledBgColor={
          isActive ? "var(--negative-act)" : "var(--positive-act)"
        }
        disabledBgColor="var(--primary-act)"
        icon={
          isActive
            ? ICONS?.REJECT_ICON_RED_WITH_WHITE_CIRCLE
            : ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE
        }
        iconToBeAlign="right"
        fullWidthRequired={fullWidthRequired}
        isDisabled={!hasCTransactionCategory}
      />
    </>
  );

  const renderField = (field) => {
    const { name, data_type: type } = field;
    const optionalSuffix = field.mandatory === "Y" ? "" : " (optional)";
    const label = `${name}${optionalSuffix}`;
    const value = additionalData[name] || "";
    const update = (val) => setField(name, val, type);
    const isHyperlink = type === "hyperlink";

    switch (type) {
      case "string":
      case "numeric":
      case "hyperlink":
        return (
          <TextInputField
            value={value}
            placeholder={isHyperlink ? name : label}
            name={isHyperlink ? `hyperLink-${name}_${type}` : name}
            fontKind="big"
            bgColor="var(--primary-card)"
            fgColor="var(--text-primary)"
            variant="default"
            adornment={isHyperlink ? ICONS?.CLOSE : ""}
            isFullWidth
            onInput={(e) => update(e.target.value)}
            onAdornmentClick={isHyperlink ? () => removeField(name) : undefined}
          />
        );

      case "list":
      case "day":
      case "month":
      case "year":
        return (
          <Field label={label}>
            <BigSelect
              options={
                type === "list" ? field.data || [] : STATIC_OPTIONS[type]
              }
              selected={value}
              setSelected={update}
              disabled={type === "list" ? field.disabled : false}
            />
          </Field>
        );

      case "time": {
        const keyTime = `${name}_${type}`;
        const time = timeData[keyTime];
        const hasTime = Boolean(time?.hours) && Boolean(time?.minutes);
        return (
          <Field label={label} className="w-width-full">
            <CommonTimePicker
              handleTimeConfirm={handleTimeConfirm}
              handleClearTime={handleClearTime}
              data_type={type}
              fieldname={name}
              value={hasTime ? value : ""}
              keyTime={keyTime}
              timeData={timeData}
              setTimeData={setTimeData}
            />
          </Field>
        );
      }

      case "toggle": {
        const state = Boolean(additionalData[name] ?? field.value);
        return (
          <Field label={label}>
            <CommonToggleCard
              description={field.description}
              state={state}
              isDisabled={field.disabled}
              handleClick={() => update(!state)}
            />
          </Field>
        );
      }

      case "upload":
        return (
          <Field
            label={`${name === "File" ? TEXT?.UPLOAD_FILE : name}${optionalSuffix}`}
          >
            <CommonFileUpload
              id={`${name}_${type}`}
              files={value || []}
              setFiles={(file) => handleFileChange(file, field)}
              isDisabled={false}
              type="all"
            />
          </Field>
        );

      case "multi_select_list":
        return (
          <Field label={label}>
            <MultiSelectDropDownComponent
              options={field.data || []}
              selected={additionalData[name] || []}
              setSelected={update}
              isOpen={false}
              showSearch={false}
              placeholder="Select"
              disabled={false}
              type="big"
            />
          </Field>
        );

      case "quantity":
        return (
          <Field label={label} className="w-width-full">
            <CommonQuantity
              quantity={additionalData[name] || 0}
              setQuantity={update}
              isDisabled={false}
            />
          </Field>
        );

      case "from_year_to_year": {
        const range = additionalData[name] || {};
        return (
          <div className="flex gap-gap-md w-width-full">
            {["from", "to"].map((edge) => (
              <Field key={edge} label={label} className="w-width-md">
                <BigSelect
                  options={YEARS}
                  selected={range[edge] || ""}
                  setSelected={(val) => update({ ...range, [edge]: val })}
                />
              </Field>
            ))}
          </div>
        );
      }

      case "month_year": {
        const monthYear = additionalData[name] || {};
        return (
          <div className="flex gap-gap-md w-width-full">
            {[
              { part: "month", options: MONTHS },
              { part: "year", options: YEARS },
            ].map(({ part, options }) => (
              <Field
                key={part}
                label={label}
                className="flex-1 min-w-min-width-base"
              >
                <BigSelect
                  options={options}
                  selected={monthYear[part] || ""}
                  setSelected={(val) => update({ ...monthYear, [part]: val })}
                />
              </Field>
            ))}
          </div>
        );
      }

      case "date":
      case "date_range": {
        const isDateRange = type === "date_range";
        const range = dateRanges[name] || {};

        return (
          <Field label={label} className="w-width-full">
            <CommonCalendar
              selectionType={isDateRange ? "range" : "single"}
              type="big"
              startDate={isDateRange ? (range.start ?? null) : ""}
              endDate={isDateRange ? (range.end ?? null) : undefined}
              handleDateChange={(dateOrRange) =>
                isDateRange
                  ? handleDateRangeChange(dateOrRange, field)
                  : update(formatDate(dateOrRange))
              }
            />
          </Field>
        );
      }

      default:
        return null;
    }
  };

  const renderAdditionalFields = () => {
    if (!fields?.length) {
      return (
        <div className="text-text-secondary text-font-xs">
          {TEXT?.CATEGORY_HAS_NO_ADDITIONAL_FIELDS}
        </div>
      );
    }
    return fields.map((field, index) => (
      <Fragment key={`${field.name}-${index}`}>{renderField(field)}</Fragment>
    ));
  };

  const statusMessage = !canManage
    ? TEXT?.DEFAULT_CATEGORY_NOT_EDITABLE
    : isActive
      ? TEXT?.DEACTIVATE_THIS_CATEGORY_MESSAGE
      : TEXT?.ACTIVATE_THIS_CATEGORY_MESSAGE;

  useEffect(() => {
    setAdditionalData({});
    setTimeData({});
    setDateRanges({});
  }, [categories]);

  // -------------------------------------------------------------------------
  // JSX
  // -------------------------------------------------------------------------
  return (
    <>
      {(deactivatePopUp || activatePopUp) && (
        <div className="fixed top-top-base left-left-base w-full-width h-full-height z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <SmallPopupWithBulletPoints
            headerTitle={
              activatePopUp
                ? TEXT?.ACTIVATE_CATEGORY_POPUP_MAIN_MESSAGE
                : TEXT?.DEACTIVATE_CATEGORY_POPUP_MAIN_MESSAGE
            }
            headerFontWeight="semibold"
            headerFontColor={
              activatePopUp ? "var(--custom-orange)" : "var(--text-primary)"
            }
            textSize={activatePopUp ? "font-lg" : "font-md"}
            subtextwithbullet="N"
            subtext={
              activatePopUp
                ? TEXT?.CATEGORY_ACTIVATION_SUBTEXT
                : TEXT?.CATEGORY_DEACTIVATION_SUBTEXT
            }
            bulletPoints={
              activatePopUp ? "" : TEXT?.CATEGORY_DEACTIVATION_IMPACTS
            }
            {...cancelButtonProps(() =>
              activatePopUp
                ? setActivatePopUp(false)
                : setDeactivatePopUp(false),
            )}
            btnTwoText={activatePopUp ? TEXT?.ACTIVATE : TEXT?.DEACTIVATE}
            btnTwoBtnType="button"
            btnTwoHandleFunction={handleCategoryStatusChange}
            btnTwoEnabledBgColor={
              activatePopUp ? "var(--positive-act)" : "var(--negative-act)"
            }
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={false}
            btnTwoHoverOption="black"
          />
        </div>
      )}

      {categoriesLoading ? (
        <div className="fixed w-screen h-screen flex justify-center items-center">
          <Loader />
        </div>
      ) : (
        <div className="h-full-height relative text-text-primary flex flex-col overflow-hidden">
          {/* Header */}
          <div className="w-width-full flex flex-col sticky top-top-base z-z-index-tooltip bg-primary-nav">
            <div className="h-navbar-breadcrumb-height Qsmd:block hidden"></div>
            <div className="w-width-full h-auto">
              <div className="max-w-tab-width-normal mx-auto w-width-full flex Qsmd:flex-row flex-col">
                <div className="flex Qsmd:hidden gap-gap-md py-padding-sm shadow-box-shadow-bottom">
                  <div
                    className="w-icon-sm h-icon_w-icon-sm items-center ml-margin-sm flex cursor-pointer"
                    onClick={() => navigate(GlobalConfiguration?.navigate_back)}
                  >
                    <SVGComponent
                      url={ICONS?.ARROW_LEFT_WHITE}
                      height="var(--icon-sm)"
                      width="var(--icon-sm)"
                      color="var(--text-primary)"
                      alt="Arrow Left White"
                    />
                  </div>
                  <div className="flex flex-col gap-gap-xs">
                    <p className="text-font-md text-text-primary font-normal">
                      {TEXT?.CATEGORIES}
                    </p>
                    <p className="text-font-xs text-text-secondary font-normal">
                      {TEXT?.VIEW_DETAILS}
                    </p>
                  </div>
                </div>

                <div className="Qsmd:flex w-width-full items-center justify-between py-padding-sm px-padding-md Qlg:px-padding-base gap-x-gap-sm">
                  <p className="text-text-primary text-font-xs flex-1">
                    {statusMessage}
                  </p>
                  {canManage && (
                    <div className="hidden Qmd:flex flex-row gap-x-gap-sm bottom-bottom-base flex-1 justify-end">
                      {renderActionButtons(false)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div
            className={`overflow-y-auto h-full flex flex-col flex-1 items-center relative w-width-full Qmd:pb-padding-md ${
              !hasAdmin && category?.custom_category === "N"
                ? "pb-padding-md"
                : "pb-[calc(var(--stepper-moble-height)+var(--padding-md))]"
            }`}
          >
            <div className="max-w-tab-width-normal w-width-full flex flex-col Qsmd:flex-row gap-x-gap-xl gap-y-gap-md pt-padding-md Qmd:px-padding-md Qlg:px-padding-base">
              {/* Details card */}
              <div className="flex flex-col w-full gap-gap-md">
                <div className="px-padding-md Qmd:px-padding-base">
                  <div className="flex flex-col bg-primary-card w-width-full p-padding-md gap-y-gap-md rounded-border-radius-md">
                    <p className="text-text-primary text-font-md font-semibold">
                      {TEXT?.CATEGORY_DETAILS}
                    </p>
                    <TitleSubtitlePair
                      title={category?.name}
                      titleColor="text-primary"
                      subtitle={TEXT?.NAME}
                      variant="md"
                      isBold={false}
                    />
                    <div className="flex items-center justify-between">
                      <TitleSubtitlePair
                        title={isActive ? TEXT?.ACTIVE : TEXT?.INACTIVE}
                        titleColor={isActive ? "settled" : "failed"}
                        subtitle={TEXT?.STATUS}
                        variant="md"
                        isBold
                      />
                      <div className="flex items-center bg-text-primary p-padding-sm rounded-border-radius-sm">
                        {categoryIcon.isSvg ? (
                          <SVGComponent
                            url={categoryIcon.url}
                            height="var(--icon-md)"
                            width="var(--icon-md)"
                            color={categoryIcon.colour}
                            alt={category?.name}
                          />
                        ) : (
                          <img
                            className="w-icon-md h-icon-md"
                            src={categoryIcon.url}
                            alt={category?.name}
                          />
                        )}
                      </div>
                    </div>
                    <TitleSubtitlePair
                      title={
                        category?.custom_category === "Y"
                          ? TEXT?.CUSTOM
                          : TEXT?.DEFAULT
                      }
                      titleColor="text-primary"
                      subtitle={TEXT?.TYPE}
                      variant="md"
                      isBold={false}
                    />
                    <TitleSubtitlePair
                      title={category?.description ?? "Not given"}
                      titleColor="text-primary"
                      subtitle={TEXT?.DESCRIPTION}
                      variant="md"
                      isBold={false}
                    />
                  </div>
                </div>
                <div className=" px-padding-md Qmd:px-padding-base">
                  <div className="flex flex-col bg-primary-card w-width-full p-padding-md gap-y-gap-md rounded-border-radius-md">
                    <div className="gap-gap-md flex flex-col">
                      <p className="text-text-primary text-font-md font-semibold">
                        {globalTextFormat(
                          TEXT?.SUPPORT_DETAILS?.toLowerCase(),
                          [0, 1],
                        )}
                      </p>

                      <div className="flex flex-row items-start justify-between">
                        <div className="flex flex-col gap-gap-xs">
                          <p className="text-text-primary text-font-md">{id}</p>
                          <p className="text-text-secondary text-font-xs">
                            Category ID
                          </p>
                        </div>
                        <div>
                          <CardActionableButton
                            icon={
                              categoryId === "category_id"
                                ? ICONS.SELECTED_WITH_GREEN_CIRCLE
                                : ICONS?.COPY
                            }
                            bgColor="var(--primary-nav-act)"
                            title={
                              categoryId === "category_id"
                                ? TEXT?.COPIED
                                : TEXT?.COPY_TO_CLIPBOARD
                            }
                            handleFunction={handleCopyIconText}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional fields preview */}
              <div className="relative w-width-full bg-primary-bg flex">
                <div className="w-width-full flex flex-col">
                  <div className="px-padding-md Qmd:px-padding-base w-auto h-height-full">
                    <div className="bg-primary-card rounded-border-radius-md w-width-full p-padding-md Qsmd:pb-padding-md pb-padding-xl">
                      <div className="text-text-primary font-semibold text-font-md">
                        {Object.keys(category?.configuration || {}).length === 0
                          ? TEXT?.NO_ADDITIONAL_FIELDS_CREATED
                          : TEXT?.ADDITIONAL_DETAILS_PREVIEW}
                      </div>
                      <div className="w-width-full flex flex-col gap-gap-md mt-margin-md">
                        {renderAdditionalFields()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile action bar */}
          {canManage && (
            <div className="flex Qmd:hidden fixed gap-x-gap-sm bottom-bottom-base p-padding-md bg-primary-nav rounded-t-border-radius-md w-width-full z-z-index-dropdown">
              {renderActionButtons(true)}
            </div>
          )}
          <div className="h-[var(--responsive-appbar-height-mobile)] Qmd:hidden w-full"></div>
        </div>
      )}
    </>
  );
};

export default CustomCategoryDetails;
